import { TValidationItem } from '@mgm/common/components/password';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from './useAppStore';
import { commonActions } from '../store/common/slice';
import { getPasswordValidation } from '../store/common/selectors';

export function useValidation() {
  const dispatch = useAppDispatch();

  const isUppercaseChar = (v: string) => {
    if (v) {
      if (/[A-Z]/.test(v)) {
        dispatch(commonActions.setPasswordValidation({
          key: 'isUpperChar',
          value: true,
        }));
        return true;
      }
      dispatch(commonActions.setPasswordValidation({
        key: 'isUpperChar',
        value: false,
      }));
      return true;
    }

    return false;
  };

  const isLowercaseChar = (v: string) => {
    if (v) {
      if (/[a-z]/.test(v)) {
        dispatch(commonActions.setPasswordValidation({
          key: 'isLowerChar',
          value: true,
        }));
        return true;
      }
      dispatch(commonActions.setPasswordValidation({
        key: 'isLowerChar',
        value: false,
      }));
      return true;
    }

    return false;
  };

  const isSpecialCharacter = (v: string) => {
    if (v) {
      if (/[!@#$%^&*(),.?":{}|<>]/.test(v)) {
        dispatch(commonActions.setPasswordValidation({
          key: 'isSpecialChar',
          value: true,
        }));
        return true;
      }
      dispatch(commonActions.setPasswordValidation({
        key: 'isSpecialChar',
        value: false,
      }));
      return true;
    }

    return false;
  };

  const isEightCharacter = (v: string) => {
    if (v) {
      if (v.length >= 8) {
        dispatch(commonActions.setPasswordValidation({
          key: 'isEightChar',
          value: true,
        }));
        return true;
      }
      dispatch(commonActions.setPasswordValidation({
        key: 'isEightChar',
        value: false,
      }));
      return true;
    }

    return false;
  };

  const isMaxCharacter = (v: string) => {
    if (v) {
      if (v.length >= 8 && v.length <= 16) {
        dispatch(commonActions.setPasswordValidation({
          key: 'isMaxChar',
          value: true,
        }));
        return true;
      }
      dispatch(commonActions.setPasswordValidation({
        key: 'isMaxChar',
        value: false,
      }));
      return true;
    }

    return false;
  };

  const isPasswordVerify = (password: string, v: string) => {
    if (v) {
      if (password?.localeCompare(v) === 0
      ) {
        dispatch(commonActions.setPasswordValidation({
          key: 'isPasswordVerify',
          value: true,
        }));
        return true;
      }
      dispatch(commonActions.setPasswordValidation({
        key: 'isPasswordVerify',
        value: false,
      }));
      return true;
    }
    return false;
  };

  const isRequiredField = <T>(value: T) => value === null || value === undefined || value === '';

  return {
    isPasswordVerify,
    isMaxCharacter,
    isLowercaseChar,
    isEightCharacter,
    isSpecialCharacter,
    isUppercaseChar,
    isRequiredField,
  };
}

export function usePasswordValidation(isVerifyPassword = false) {
  const { t } = useTranslation('welcome');
  const passwordValidation = useAppSelector(getPasswordValidation);

  const validationItems = useMemo(() => {
    const items = [
      {
        text: t('validation.oneLowerCharacter'),
        value: passwordValidation.isLowerChar,
      },
      {
        text: t('validation.oneUppercaseCharacter'),
        value: passwordValidation.isUpperChar,
      },
      {
        text: t('validation.oneSpecialCharacter'),
        value: passwordValidation.isSpecialChar,
      },
      {
        text: t('validation.minCharacters'),
        value: passwordValidation.isEightChar,
      },
      {
        text: t('validation.maxCharacters'),
        value: passwordValidation.isMaxChar,
      },
      isVerifyPassword && {
        text: t('validation.verifyPassword'),
        value: passwordValidation.isPasswordVerify,
      },
    ].filter((x) => !!x);

    return items as Array<TValidationItem>;
  }, [passwordValidation, t, isVerifyPassword]);

  return {
    validationItems,
  };
}
